/**
 * FormPresetPicker — pick a starting point for a Digital Form.
 *
 * Shown when an admin switches a template to "Digital form" and the
 * form_schema is still empty. Presents a grid of standard HR forms
 * (Emergency Contact, Direct Deposit, I-9, etc.) plus a "Start blank"
 * tile for the rare case the admin wants to build something custom
 * from scratch.
 *
 * Selecting a preset hands the parent its full ``FormField[]`` schema
 * — once loaded into the FormBuilder, every field is editable and
 * removable, and admins can append their own custom fields. There is
 * no notion of a "locked" preset; this component is purely a
 * convenient launchpad.
 *
 * Emits the optional ``presetMeta`` (key, name, default flags) so the
 * parent settings form can prefill name / category / required /
 * signature / deadline_days when a preset is chosen. The parent
 * decides whether to actually apply those defaults — this component
 * only surfaces them.
 */
import { useMemo, useState } from "react";
import { FilePlus2, FileText, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FORM_PRESETS, type FormPreset } from "./presets";
import type { FormField } from "./types";

const CATEGORY_LABELS: Record<FormPreset["category"], string> = {
  onboarding: "Onboarding",
  compliance: "Compliance",
  policy: "Policy",
  benefits: "Benefits",
  tax: "Tax",
  other: "Other",
};

interface FormPresetPickerProps {
  /**
   * Called when the admin picks a preset OR "Start blank". For blank,
   * ``schema`` is `[]` and ``presetMeta`` is undefined. For a preset,
   * the full schema and the preset's metadata (name, category,
   * default flags) are passed so the parent can prefill fields.
   */
  onPick: (schema: FormField[], presetMeta?: FormPreset) => void;
  /** Optional dismiss action — used when shown inside a modal. */
  onCancel?: () => void;
}

export function FormPresetPicker({ onPick, onCancel }: FormPresetPickerProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FORM_PRESETS;
    return FORM_PRESETS.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        CATEGORY_LABELS[p.category].toLowerCase().includes(q),
    );
  }, [query]);

  // Group by category for visual scanning.
  const grouped = useMemo(() => {
    const out = new Map<FormPreset["category"], FormPreset[]>();
    for (const p of filtered) {
      const arr = out.get(p.category) ?? [];
      arr.push(p);
      out.set(p.category, arr);
    }
    return Array.from(out.entries());
  }, [filtered]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-text-primary inline-flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-accent" />
            Start from a template
          </p>
          <p className="text-xs text-text-muted mt-0.5">
            Pick a standard HR form to load with sensible default fields.
            You can add, remove, or edit anything afterward.
          </p>
        </div>
        <div className="w-full sm:w-56">
          <Input
            placeholder="Search presets…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Start-blank tile sits above the grouped grid so admins who
          know they want a custom form don't have to scroll. */}
      <button
        type="button"
        onClick={() => onPick([])}
        className="w-full text-left rounded-md border border-dashed border-border/60 bg-surface hover:bg-surface-hover hover:border-accent/40 transition-colors px-3 py-3"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-accent/10 p-2 text-accent">
            <FilePlus2 className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-text-primary">
              Start blank
            </div>
            <div className="text-xs text-text-muted mt-0.5">
              Build from scratch with the form builder.
            </div>
          </div>
        </div>
      </button>

      {grouped.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/60 px-4 py-6 text-center">
          <p className="text-sm text-text-muted">
            No presets match &ldquo;{query}&rdquo;.
          </p>
        </div>
      ) : (
        grouped.map(([cat, items]) => (
          <div key={cat}>
            <p className="text-[11px] uppercase tracking-wide text-text-muted mb-2">
              {CATEGORY_LABELS[cat]}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {items.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => onPick(preset.schema, preset)}
                  className="text-left rounded-md border border-border/60 bg-surface hover:bg-surface-hover hover:border-accent/40 transition-colors px-3 py-3"
                >
                  <div className="flex items-start gap-3">
                    <div className="rounded-md bg-accent/10 p-2 text-accent shrink-0">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary">
                        {preset.name}
                      </div>
                      <div className="text-xs text-text-muted mt-0.5 line-clamp-2">
                        {preset.description}
                      </div>
                      <div className="text-[10px] text-text-muted mt-1">
                        {preset.schema.filter(
                          (f) => f.type !== "section_header",
                        ).length}{" "}
                        fields
                        {preset.default_requires_signature
                          ? " · Signature"
                          : ""}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))
      )}

      {onCancel && (
        <div className="flex justify-end pt-2 border-t border-border/40">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

export default FormPresetPicker;
